/** Profile handles (business / professional). */
export const CUSTOM_URL_MAX_LENGTH = 255;

/** Film / series OTT slug (matches backend SLUG_MAX_LENGTH). */
export const CUSTOM_NAME_MAX_LENGTH = 80;

/** Profiles: letters and numbers only. */
const CUSTOM_URL_ALLOWED_REGEX = /^[a-zA-Z0-9]+$/;

/**
 * Films / series: lowercase letters, digits, hyphens — mirrors backend
 * SLUG_PATTERN `^[a-z0-9][a-z0-9-]*$`.
 */
const CUSTOM_NAME_ALLOWED_REGEX = /^[a-z0-9][a-z0-9-]*$/;

export const CUSTOM_NAME_LINK_ERROR =
  "Enter a short name (e.g. my-film-title) — not a Google Drive, YouTube, or other link.";

export const CUSTOM_NAME_CHARS_ERROR =
  "Custom name may only contain lowercase letters, digits and hyphens, and must start with a letter or digit.";

/**
 * True when the value looks like a pasted URL / file link rather than a slug.
 * Checked on the raw input before sanitization.
 */
export const looksLikeUrlOrLink = (value?: string) => {
  if (!value) return false;

  const v = String(value).trim().toLowerCase();
  if (!v) return false;

  if (
    v.startsWith("http://") ||
    v.startsWith("https://") ||
    v.startsWith("www.") ||
    v.includes("://") ||
    v.includes("/")
  ) {
    return true;
  }

  // Domain-like values (e.g. drive.google.com) without a scheme.
  if (v.includes(".") && /[a-z0-9-]+\.[a-z]{2,}/i.test(v)) {
    return true;
  }

  return false;
};

/** Sanitize profile custom URLs (alphanumeric only). */
export const sanitizeCustomUrl = (
  value?: string,
  maxLength: number = CUSTOM_URL_MAX_LENGTH
) => {
  if (!value) return "";

  return value.replace(/[^a-zA-Z0-9]/g, "").slice(0, maxLength);
};

/**
 * Sanitize film/series custom_name: lowercase, keep letters/digits/hyphens,
 * collapse spaces to hyphens, strip other characters.
 */
export const sanitizeCustomName = (
  value?: string,
  maxLength: number = CUSTOM_NAME_MAX_LENGTH
) => {
  if (!value) return "";

  return String(value)
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, maxLength);
};

export const isInvalidCustomUrl = (value?: string) => {
  if (!value) return false;

  return (
    looksLikeUrlOrLink(value) || !CUSTOM_URL_ALLOWED_REGEX.test(value)
  );
};

/** Field-level error for film/series custom_name; null when valid or empty. */
export const getCustomNameValidationError = (value?: string): string | null => {
  if (!value) return null;

  const v = String(value).trim();
  if (!v) return null;

  if (looksLikeUrlOrLink(v)) {
    return CUSTOM_NAME_LINK_ERROR;
  }

  if (v.length > CUSTOM_NAME_MAX_LENGTH) {
    return `Max. limit is ${CUSTOM_NAME_MAX_LENGTH} characters`;
  }

  if (!CUSTOM_NAME_ALLOWED_REGEX.test(v)) {
    return CUSTOM_NAME_CHARS_ERROR;
  }

  return null;
};
